import { Box, css, Divider, Grid, Stack, Typography } from '@mui/material';
import { useState, useEffect } from 'react';

import { useLayerDashboardTabStore } from '../context/LayerDashboardContext';
import { eliminateArrayDuplicates } from 'utils/arrayUtilts';
import { TitleChart } from './TitleChart';

const styles = css({
    overflow: 'auto',
    height: '100%',
    boxSizing: 'border-box',
    '& .summary-info-by-dashboard': {
        display: 'flex',
        flexDirection: 'column',
        padding: '5px',
        height: '100%',
        boxSizing: 'border-box',
    },
    '& .summary-info-by-dashboard > .MuiTypography-root': {
        flex: '0 0 auto',
        fontWeight: 'bold',
        marginBottom: '8px',
    },
    '& .summary-info-box': {
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'flex-start',
        alignItems: 'center',
        height: '100%',
        padding: '5px',
        border: '1px solid #ddd',
        borderRadius: '8px',
        boxSizing: 'border-box',
    },
    '& .summary-info-box .MuiTypography-root:first-of-type': {
        fontSize: '0.85rem',
        color: '#555',
    },
    '& .summary-info-box .MuiTypography-root:last-of-type': {
        fontSize: '1.25rem',
        fontWeight: 'bold',
        marginTop: 'auto',
    }
});

export const SummaryInfo = ({ summaryInfo }: SummaryInfoProps) => {
    const { data, displayableData } = useLayerDashboardTabStore(state => ({
        data: state.data,
        displayableData: state.displayableData
    }));

    const [dashboardNames, setDashboardNames] = useState<string[]>();
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [chartFunctions, setChartFunctions] = useState<any[]>([]);

    const normalizeText = (text: string) => {
        if (text === null) {
            return text;
        }
        return text.replace(' ', '').toLowerCase();
    };

    const getDashboardItemsQuantity = (dashboardName: string) => {
        let values = displayableData.filter(x => normalizeText(x['layer_title']) === normalizeText(dashboardName)).length;
        return values;
    };

    const wellsQuantity = () => {
        let wellsQtd = eliminateArrayDuplicates(displayableData.map(x  => x['well_id'])).length;
        return wellsQtd;
    };

    const sideSamplesQuantity = () => {
        let sideSamplesQtd = eliminateArrayDuplicates(displayableData.filter(x => normalizeText(x['tipo_amostra']) === normalizeText('Amostra Lateral'))).length;
        return sideSamplesQtd;
    };

    const gutterSamplesQuantity = () => {
        let gutterSamplesQtd = eliminateArrayDuplicates(displayableData.filter(x => normalizeText(x['tipo_amostra']) === normalizeText('Amostra de Calha'))).length;
        return gutterSamplesQtd;
    };

    const coreQuantity = () => {
        let coreQtd = eliminateArrayDuplicates(displayableData.filter(x => normalizeText(x['tipo_amostra']) === normalizeText('fragmento de testemunho'))).length;
        return coreQtd;
    };

    const coreMeters = () => {
        let fragments = displayableData.filter(x => normalizeText(x['tipo_amostra']) === normalizeText('fragmento de testemunho'));
        let gruped: Record<string, number[]> = fragments.reduce((acc, item) => {
            if (!acc[item.well_id]) {
                acc[item.well_id] = [];
            }
            acc[item.well_id].push(item.profundidade);
            return acc;
        }, {});

        let results = Object.entries(gruped).map(([well_id, depths]) => {
            let min = Math.min(...depths);
            let max = Math.max(...depths);
            let interval = max - min;
            return { well_id, min, max, interval };
        });

        let sumIntervals = results.reduce((acc, r) => acc + r.interval, 0);

        return sumIntervals.toFixed(2);
    };

    const coreSamplesQuantity = () => {
        let coreSamplesQtd = eliminateArrayDuplicates(displayableData.filter(x => normalizeText(x['tipo_amostra']) === normalizeText('Plugue de Testemunho'))).length;
        return coreSamplesQtd;
    };

    const outcropSamplesQuantity = () => {
        let outcropSamplesQtd = eliminateArrayDuplicates(displayableData.filter(x => normalizeText(x['tipo_amostra']) === normalizeText('Amostra de Afloramento'))).length;
        return outcropSamplesQtd;
    };

    const thinBladesQuantity = () => {
        let thinBladesQtd = eliminateArrayDuplicates(displayableData.filter(x => normalizeText(x['tipo_amostra']) === normalizeText('Lâminas Delgadas'))).length;
        return thinBladesQtd;
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const fns: any = { wellsQuantity, sideSamplesQuantity, gutterSamplesQuantity, coreQuantity, coreMeters, coreSamplesQuantity, outcropSamplesQuantity, thinBladesQuantity };

    useEffect(() => {
        if (data) {
            let values = eliminateArrayDuplicates(data.map(item => item['layer_title'])).filter(x => x !== null);
            setDashboardNames(values);
        }
    }, [data]);

    useEffect(() => {
        if (summaryInfo !== '') {
            const parsed = JSON.parse(summaryInfo);
            setChartFunctions(parsed);
        }
    }, [summaryInfo]);

    return (
        <Stack css={styles} direction={'column'}>
            <TitleChart title='Filter' maximizeOperation={''} />
            <Stack direction={'row'} alignItems="flex-start" height='100%'>
                <Box className='summary-info-by-dashboard' width='40%'>
                    <Typography>Resumo dos Datasets</Typography>
                    <Grid container spacing={2}>
                        {dashboardNames?.map((item) => (
                            <Grid item xs={12} sm={4} key={item}>
                                <Box className='summary-info-box'>
                                    <Typography align='center'>{item}</Typography>
                                    <Typography>{getDashboardItemsQuantity(item)}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
                <Divider orientation="vertical" flexItem sx={{marginTop: 1}}/>
                <Box className='summary-info-by-dashboard' width='60%'>
                    <Typography>Amostras Analisadas</Typography>
                    <Grid container  spacing={2}>
                        {chartFunctions.map((item) => (
                            <Grid item xs={12} sm={3}>
                                <Box className='summary-info-box'>
                                    <Typography>{item['Label']}</Typography>
                                    <Typography>{fns[item['Function']]()}</Typography>
                                </Box>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            </Stack>
        </Stack>
    );
};

interface SummaryInfoProps {
    summaryInfo: string;
}